import { useEffect, useState } from "react";
import { toast } from "sonner";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { BESPOKE_ICONS, ROLE_LABELS } from "../../../lib/bespoke";

const EMPTY = { name: "", key: "", description: "", notes: "", one_off_fee: "", monthly_fee: "", status: "draft", icon: "PuzzlePiece", roles: ["super_admin", "admin", "project_manager", "team_member", "in_house"] };
const slug = (s) => (s || "").toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

/** Create or edit a bespoke module. `fromRequest` pre-fills from a tenant request and links it on save. */
export function ModuleDialog({ open, onOpenChange, module, fromRequest, roles = Object.keys(ROLE_LABELS), onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [keyTouched, setKeyTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const editing = !!module;

  useEffect(() => {
    if (!open) return;
    if (module) setForm({ ...EMPTY, name: module.name, key: module.key, description: module.description || "", notes: module.notes || "", status: module.status, icon: module.icon || "PuzzlePiece", roles: module.roles || EMPTY.roles, one_off_fee: module.one_off_fee ?? "", monthly_fee: module.monthly_fee ?? "" });
    else if (fromRequest) setForm({ ...EMPTY, name: fromRequest.title, key: slug(fromRequest.title), description: fromRequest.details.slice(0, 600), one_off_fee: fromRequest.quote?.one_off_fee ?? "", monthly_fee: fromRequest.quote?.monthly_fee ?? "", requested_by_tenant_id: fromRequest.tenant_id });
    else setForm(EMPTY);
    setKeyTouched(!!module);
  }, [open, module, fromRequest]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setName = (e) => setForm((f) => ({ ...f, name: e.target.value, key: keyTouched || editing ? f.key : slug(e.target.value) }));
  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the module a name");
    setBusy(true);
    try {
      const body = { ...form, one_off_fee: form.one_off_fee === "" ? 0 : Number(form.one_off_fee), monthly_fee: form.monthly_fee === "" ? 0 : Number(form.monthly_fee) };
      if (fromRequest && !editing) body.request_id = fromRequest.id;
      const { data } = editing ? await API.put(`/platform/bespoke/modules/${module.id}`, body) : await API.post("/platform/bespoke/modules", body);
      toast.success(editing ? "Module updated" : `Module '${data.name}' created`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the module");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bespoke-module-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">{editing ? `Edit ${module.name}` : "New bespoke module"}</DialogTitle>
          <DialogDescription>{editing ? "Fees here are the defaults offered when the module is added to a tenant — existing tenants keep their own." : "A module is a menu entry we build for a customer. Create it here, then add it to the tenant(s) who pay for it."}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label className="text-xs">Module name *</Label><Input value={form.name} onChange={setName} placeholder="e.g. Pop's Waste List" className="mt-1" data-testid="bespoke-name-input" /></div>
          <div>
            <Label className="text-xs">Key {editing ? "(fixed)" : "*"}</Label>
            <Input value={form.key} onChange={(e) => { setKeyTouched(true); setForm((f) => ({ ...f, key: slug(e.target.value) })); }} disabled={editing} className="mt-1 font-mono" data-testid="bespoke-key-input" />
            <p className="text-[10px] text-muted-foreground mt-1">Used in code and the URL: /modules/<b>{form.key || "key"}</b></p>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select value={form.status} onChange={set("status")} className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="bespoke-status-select">
              <option value="draft">Being built (hidden from menu)</option>
              <option value="live">Live (in the tenant's menu)</option>
              <option value="retired">Retired (no new tenants)</option>
            </select>
          </div>
          <div><Label className="text-xs">Default one-off fee (£)</Label><Input type="number" min="0" step="0.01" value={form.one_off_fee} onChange={set("one_off_fee")} placeholder="0.00" className="mt-1" data-testid="bespoke-oneoff-input" /></div>
          <div><Label className="text-xs">Default monthly fee (£)</Label><Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={set("monthly_fee")} placeholder="0.00" className="mt-1" data-testid="bespoke-monthly-input" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">What it does (shown to the tenant)</Label><Textarea value={form.description} onChange={set("description")} rows={2} maxLength={600} className="mt-1" data-testid="bespoke-description-input" /></div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Icon</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="bespoke-icon-picker">
              {Object.entries(BESPOKE_ICONS).map(([name, Icon]) => (
                <button key={name} type="button" onClick={() => setForm((f) => ({ ...f, icon: name }))} title={name}
                  className={`h-9 w-9 rounded-md border flex items-center justify-center transition-colors ${form.icon === name ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`} data-testid={`bespoke-icon-${name}`}>
                  <Icon size={18} weight={form.icon === name ? "fill" : "regular"} />
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Who sees it in the tenant's menu</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="bespoke-roles-picker">
              {roles.map((r) => (
                <button key={r} type="button" onClick={() => r !== "super_admin" && toggleRole(r)} disabled={r === "super_admin"}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${form.roles.includes(r) ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-muted"} disabled:opacity-70`} data-testid={`bespoke-role-${r}`}>
                  {ROLE_LABELS[r] || r}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs">Internal notes (spec, who asked, links)</Label><Textarea value={form.notes} onChange={set("notes")} rows={3} maxLength={2000} className="mt-1" data-testid="bespoke-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="bespoke-module-save-btn">{busy ? "Saving…" : editing ? "Save changes" : "Create module"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
